// @file For special effects for whiteboard
import { __ } from '@@/bits/intl'
import { deleteShapeMetadata } from '@@/bits/whiteboard'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { ShapeSpecialEffects } from '@@/pinia/whiteboard_special_effects'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { isEmpty } from 'es-toolkit/compat'
import type { JsonObject, TLShape, TLShapeId } from 'tldraw'

/**
 * Apply special effects when grouping multiple shapes:
 * 1. If all shapes have the same effect, apply that effect to the group shape.
 * 2. If shapes have different effects, remove all effects and show undo option.
 * 3. If no shapes have effects, do nothing.
 * @param {TLShape[]} childShapes - The shapes being grouped
 * @param {TLShape} groupShape - The created group shape
 * @param {string} markId - The mark id to bail to if the user undoes the grouping
 */
function applyGroupSpecialEffects(childShapes: TLShape[], groupShape: TLShape, markId: string): void {
  if (childShapes.length === 0) return

  const { editor } = useTldrawEditor()
  if (editor == null) return

  const shapeEffects: Array<Array<string | null>> = childShapes.map((shape) => {
    const soundEffect = (shape?.meta?.sound_effect as ShapeSpecialEffects['sound_effect'])?.effect_id ?? null
    const visualEffect = (shape?.meta?.visual_effect as ShapeSpecialEffects['visual_effect'])?.effect_id ?? null
    return [soundEffect, visualEffect]
  })

  // Generate the most effective effect by checking the first shape's effects and then checking the rest of the shapes
  // For example, [["rec3SYMvNrFmSSEo6", null], [null, "confetti"]]
  // The effective effect should be ["rec3SYMvNrFmSSEo6", "confetti"]
  const effectiveEffect = [
    shapeEffects.find((effect) => effect[0] !== null)?.[0] ?? null,
    shapeEffects.find((effect) => effect[1] !== null)?.[1] ?? null,
  ]

  // Normalize shape effects by filling in any missing effects with the first shape's effects.
  // This allows us to properly compare effects across shapes, treating null values as matching
  // the first shape's effects. For example, if the first shape has a sound effect but no visual effect,
  // other shapes with no effects will be treated as having the same sound effect.
  const normalizedShapeEffects = shapeEffects.map(([soundEffect, visualEffect]) => ({
    soundEffect: soundEffect ?? effectiveEffect[0],
    visualEffect: visualEffect ?? effectiveEffect[1],
  }))
  const normalizedEffectiveEffect = normalizedShapeEffects[0]
  const allShapesHaveTheSameSoundEffect = new Set(normalizedShapeEffects.map((effect) => effect.soundEffect)).size === 1
  const allShapesHaveTheSameVisualEffect =
    new Set(normalizedShapeEffects.map((effect) => effect.visualEffect)).size === 1

  // Apply effects directly to the group shape
  editor.run(() => {
    let soundEffectToApply: string | null = null
    let visualEffectToApply: string | null = null
    let shouldRemoveSoundEffectsFromChildren = false
    let shouldRemoveVisualEffectsFromChildren = false

    if (allShapesHaveTheSameSoundEffect) {
      soundEffectToApply = normalizedEffectiveEffect.soundEffect
    } else {
      // If not all shapes have the same sound effect, remove the sound effect from all child shapes
      shouldRemoveSoundEffectsFromChildren = true
    }

    if (allShapesHaveTheSameVisualEffect) {
      visualEffectToApply = normalizedEffectiveEffect.visualEffect
    } else {
      // If not all shapes have the same visual effect, remove the visual effect from all child shapes
      shouldRemoveVisualEffectsFromChildren = true
    }

    // Apply the determined effects to the group shape
    updateSpecialEffectsInShape(groupShape.id, {
      soundEffectId: soundEffectToApply,
      visualEffectId: visualEffectToApply,
    })

    // Remove effects from child shapes if they had conflicting effects
    if (shouldRemoveSoundEffectsFromChildren) {
      childShapes.forEach((shape) => deleteShapeMetadata(shape.id, ['sound_effect']))
    }
    if (shouldRemoveVisualEffectsFromChildren) {
      childShapes.forEach((shape) => deleteShapeMetadata(shape.id, ['visual_effect']))
    }

    // Show undo message if effects were removed
    let undoMessage = ''
    if (shouldRemoveSoundEffectsFromChildren && shouldRemoveVisualEffectsFromChildren) {
      undoMessage = __('Special effects have been removed from objects in this group')
    } else if (shouldRemoveSoundEffectsFromChildren) {
      undoMessage = __('Sound effects have been removed from objects in this group')
    } else if (shouldRemoveVisualEffectsFromChildren) {
      undoMessage = __('Visual effects have been removed from objects in this group')
    }

    if (undoMessage !== '') {
      // Show a snackbar to allow the user to undo the grouping action
      useGlobalSnackbarStore().setSnackbar({
        message: undoMessage,
        actionText: __('Undo'),
        actionTextActions: [() => editor.bailToMark(markId)],
      })
    }
  })
}

/**
 * Updates the special effects (sound and visual) for a given shape
 * @param shapeId - The ID of the shape to update
 * @param specialEffects - Object containing the sound and visual effect IDs to apply
 * @param specialEffects.soundEffectId - ID of the sound effect to apply, or null to remove
 * @param specialEffects.visualEffectId - ID of the visual effect to apply, or null to remove
 */
function updateSpecialEffectsInShape(
  shapeId: TLShapeId,
  specialEffectIds: {
    soundEffectId?: string | null
    visualEffectId?: string | null
  },
): void {
  const { editor } = useTldrawEditor()
  if (editor == null) return
  const shape = editor.getShape(shapeId)
  if (shape == null) return

  const meta: ShapeSpecialEffects = {}
  const keysToDelete: string[] = []
  if (specialEffectIds.soundEffectId != null) {
    meta.sound_effect = { type: 'system', effect_id: specialEffectIds.soundEffectId }
  } else {
    keysToDelete.push('sound_effect')
  }
  if (specialEffectIds.visualEffectId != null) {
    meta.visual_effect = { effect_id: specialEffectIds.visualEffectId }
  } else {
    keysToDelete.push('visual_effect')
  }

  if (!isEmpty(meta)) {
    editor.updateShape({
      id: shape.id,
      type: shape.type,
      meta: meta as Partial<JsonObject>,
    })
  }

  if (keysToDelete.length > 0) {
    deleteShapeMetadata(shapeId, keysToDelete)
  }
}

/**
 * Updates the special effects (sound and visual) for all shapes within a group
 * @param groupId - The ID of the group containing the shapes to update
 * @param specialEffectIds - Object containing the sound and visual effect IDs to apply
 * @param specialEffectIds.soundEffectId - ID of the sound effect to apply, or null to remove
 * @param specialEffectIds.visualEffectId - ID of the visual effect to apply, or null to remove
 */
function updateSpecialEffectsInAllShapesInGroup(
  groupId: TLShapeId,
  specialEffectIds: {
    soundEffectId?: string | null
    visualEffectId?: string | null
  },
): void {
  const { editor } = useTldrawEditor()
  if (editor == null) return
  const shapes = editor.getCurrentPageShapes().filter((shape) => shape.parentId === groupId)
  editor.run(() => {
    shapes.forEach((shape) => {
      updateSpecialEffectsInShape(shape.id, specialEffectIds)
    })
  })
}

export { applyGroupSpecialEffects, updateSpecialEffectsInAllShapesInGroup, updateSpecialEffectsInShape }
